import { html, nothing } from "lit";
import { parseAgentSessionKey } from "../../../src/routing/session-key.js";
import { t } from "../i18n/index.ts";
import { refreshChatAvatar } from "./app-chat.ts";
import { renderUsageTab } from "./app-render-usage-tab.ts";
import { renderChatControls, renderTab, renderThemeToggle } from "./app-render.helpers.ts";
import type { AppViewState } from "./app-view-state.ts";
import { loadAgentFileContent, loadAgentFiles, saveAgentFile } from "./controllers/agent-files.ts";
import { loadAgentIdentities, loadAgentIdentity } from "./controllers/agent-identity.ts";
import { loadAgentSkills } from "./controllers/agent-skills.ts";
import { loadAgents, loadToolsCatalog } from "./controllers/agents.ts";
import { loadChannels } from "./controllers/channels.ts";
import { loadChatHistory } from "./controllers/chat.ts";
import {
  applyConfig,
  loadConfig,
  runUpdate,
  saveConfig,
  updateConfigFormValue,
  removeConfigFormValue,
} from "./controllers/config.ts";
import type { CfTask, CfKeyword } from "./controllers/contentforge.ts";
import type { ContentForgeDashboardData } from "./controllers/contentforge.ts";
import {
  loadCronRuns,
  loadMoreCronJobs,
  loadMoreCronRuns,
  reloadCronJobs,
  toggleCronJob,
  runCronJob,
  removeCronJob,
  addCronJob,
  startCronEdit,
  startCronClone,
  cancelCronEdit,
  validateCronForm,
  hasCronFormErrors,
  normalizeCronFormState,
  getVisibleCronJobs,
  updateCronJobsFilter,
  updateCronRunsFilter,
} from "./controllers/cron.ts";
import { loadDebug, callDebugMethod } from "./controllers/debug.ts";
import {
  approveDevicePairing,
  loadDevices,
  rejectDevicePairing,
  revokeDeviceToken,
  rotateDeviceToken,
} from "./controllers/devices.ts";
import {
  loadExecApprovals,
  removeExecApprovalsFormValue,
  saveExecApprovals,
  updateExecApprovalsFormValue,
} from "./controllers/exec-approvals.ts";
import { loadLogs } from "./controllers/logs.ts";
import { loadNodes } from "./controllers/nodes.ts";
import { loadPresence } from "./controllers/presence.ts";
import { deleteSessionAndRefresh, loadSessions, patchSession } from "./controllers/sessions.ts";
import {
  installSkill,
  loadSkills,
  saveSkillApiKey,
  updateSkillEdit,
  updateSkillEnabled,
} from "./controllers/skills.ts";
import { buildExternalLinkRel, EXTERNAL_LINK_TARGET } from "./external-link.ts";
import { icons } from "./icons.ts";
import {
  normalizeBasePath,
  TAB_GROUPS,
  subtitleForTab,
  titleForTab,
  MC_SIDEBAR_ITEMS,
  isMcTab,
  getWrappedTab,
  type Tab,
} from "./navigation.ts";
import { resolveConfiguredCronModelSuggestions } from "./views/agents-utils.ts";
import { renderAgents } from "./views/agents.ts";
import { renderChannels } from "./views/channels.ts";
import { renderChat } from "./views/chat.ts";
import { renderConfig } from "./views/config.ts";
import { renderCfApprovals } from "./views/contentforge-approvals.ts";
import type { CfApproval, CfApprovalCategory } from "./views/contentforge-approvals.ts";
import { renderCfCalendar } from "./views/contentforge-calendar.ts";
import { renderCfContent, type CfCsvSource } from "./views/contentforge-content.ts";
import { renderCfMemory } from "./views/contentforge-memory.ts";
import { renderCfOffice } from "./views/contentforge-office.ts";
import { renderCfProducts } from "./views/contentforge-products.ts";
import { renderCfProjects } from "./views/contentforge-projects.ts";
import type { CfProject } from "./views/contentforge-projects.ts";
import { renderCfTasks } from "./views/contentforge-tasks.ts";
import { renderContentForge, renderPinterestPanel } from "./views/contentforge.ts";
import { renderCron } from "./views/cron.ts";
import { renderDebug } from "./views/debug.ts";
import { renderExecApprovalPrompt } from "./views/exec-approval.ts";
import { renderGatewayUrlConfirmation } from "./views/gateway-url-confirmation.ts";
import { renderInstances } from "./views/instances.ts";
import { renderLogs } from "./views/logs.ts";
import { renderNodes } from "./views/nodes.ts";
import { renderOverview } from "./views/overview.ts";
import { renderSessions } from "./views/sessions.ts";
import { renderSkills } from "./views/skills.ts";

const AVATAR_DATA_RE = /^data:/i;
const AVATAR_HTTP_RE = /^https?:\/\//i;
const CRON_THINKING_SUGGESTIONS = ["off", "minimal", "low", "medium", "high"];
const CRON_TIMEZONE_SUGGESTIONS = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Tokyo",
];

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

function normalizeSuggestionValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function uniquePreserveOrder(values: string[]): string[] {
  const seen = new Set<string>();
  const output: string[] = [];
  for (const value of values) {
    const normalized = value.trim();
    if (!normalized) {
      continue;
    }
    const key = normalized.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    output.push(normalized);
  }
  return output;
}

function resolveAssistantAvatarUrl(state: AppViewState): string | undefined {
  const list = state.agentsList?.agents ?? [];
  const parsed = parseAgentSessionKey(state.sessionKey);
  const agentId = parsed?.agentId ?? state.agentsList?.defaultId ?? "main";
  const agent = list.find((entry) => entry.id === agentId);
  const identity = agent?.identity;
  const candidate = identity?.avatarUrl ?? identity?.avatar;
  if (!candidate) {
    return undefined;
  }
  if (AVATAR_DATA_RE.test(candidate) || AVATAR_HTTP_RE.test(candidate)) {
    return candidate;
  }
  return identity?.avatarUrl;
}

export function renderApp(state: AppViewState) {
  const openClawVersion =
    (typeof state.hello?.server?.version === "string" && state.hello.server.version.trim()) ||
    state.updateAvailable?.currentVersion ||
    t("common.na");
  const availableUpdate =
    state.updateAvailable &&
    state.updateAvailable.latestVersion !== state.updateAvailable.currentVersion
      ? state.updateAvailable
      : null;
  const versionStatusClass = availableUpdate ? "warn" : "ok";
  const presenceCount = state.presenceEntries.length;
  const sessionsCount = state.sessionsResult?.count ?? null;
  const cronNext = state.cronStatus?.nextWakeAtMs ?? null;
  const chatDisabledReason = state.connected ? null : t("chat.disconnected");
  const isChat = state.tab === "chat";
  const chatFocus = isChat && (state.settings.chatFocusMode || state.onboarding);
  const showThinking = state.onboarding ? false : state.settings.chatShowThinking;
  const assistantAvatarUrl = resolveAssistantAvatarUrl(state);
  const chatAvatarUrl = state.chatAvatarUrl ?? assistantAvatarUrl ?? null;
  const configValue =
    state.configForm ?? (state.configSnapshot?.config as Record<string, unknown> | null);
  const basePath = normalizeBasePath(state.basePath ?? "");
  const resolvedAgentId =
    state.agentsSelectedId ??
    state.agentsList?.defaultId ??
    state.agentsList?.agents?.[0]?.id ??
    null;
  const cronAgentSuggestions = Array.from(
    new Set(
      [
        ...(state.agentsList?.agents?.map((entry) => entry.id.trim()) ?? []),
        ...state.cronJobs
          .map((job) => (typeof job.agentId === "string" ? job.agentId.trim() : ""))
          .filter(Boolean),
      ].filter(Boolean),
    ),
  ).toSorted((a, b) => a.localeCompare(b));
  const cronModelSuggestions = Array.from(
    new Set(
      [
        ...state.cronModelSuggestions,
        ...resolveConfiguredCronModelSuggestions(configValue),
        ...state.cronJobs
          .map((job) => {
            if (job.payload.kind !== "agentTurn" || typeof job.payload.model !== "string") {
              return "";
            }
            return job.payload.model.trim();
          })
          .filter(Boolean),
      ].filter(Boolean),
    ),
  ).toSorted((a, b) => a.localeCompare(b));
  const visibleCronJobs = getVisibleCronJobs(state);
  const selectedDeliveryChannel =
    state.cronForm.deliveryChannel && state.cronForm.deliveryChannel.trim()
      ? state.cronForm.deliveryChannel.trim()
      : "last";
  const jobToSuggestions = state.cronJobs
    .map((job) => normalizeSuggestionValue(job.delivery?.to))
    .filter(Boolean);
  const accountToSuggestions = (
    selectedDeliveryChannel === "last"
      ? Object.values(state.channelsSnapshot?.channelAccounts ?? {}).flat()
      : (state.channelsSnapshot?.channelAccounts?.[selectedDeliveryChannel] ?? [])
  )
    .flatMap((account) => [
      normalizeSuggestionValue(account.accountId),
      normalizeSuggestionValue(account.name),
    ])
    .filter(Boolean);
  const rawDeliveryToSuggestions = uniquePreserveOrder([
    ...jobToSuggestions,
    ...accountToSuggestions,
  ]);
  const deliveryToSuggestions =
    state.cronForm.deliveryMode === "webhook"
      ? rawDeliveryToSuggestions.filter((value) => isHttpUrl(value))
      : rawDeliveryToSuggestions;

  // ── Mission Control Mode ──────────────────────────────────────────────
  if (isMcTab(state.tab)) {
    return renderMissionControl(state);
  }

  return html`
    <div class="shell ${isChat ? "shell--chat" : ""} ${chatFocus ? "shell--chat-focus" : ""} ${state.settings.navCollapsed ? "shell--nav-collapsed" : ""} ${state.onboarding ? "shell--onboarding" : ""}">
      <header class="topbar">
        <div class="topbar-left">
          <button
            class="nav-collapse-toggle"
            @click=${() =>
              state.applySettings({
                ...state.settings,
                navCollapsed: !state.settings.navCollapsed,
              })}
            title="${state.settings.navCollapsed ? t("nav.expand") : t("nav.collapse")}"
            aria-label="${state.settings.navCollapsed ? t("nav.expand") : t("nav.collapse")}"
          >
            <span class="nav-collapse-toggle__icon">${icons.menu}</span>
          </button>
          <div class="brand">
            <div class="brand-logo">
              <img src=${basePath ? `${basePath}/favicon.svg` : "/favicon.svg"} alt="OpenClaw" />
            </div>
            <div class="brand-text">
              <div class="brand-title">OPENCLAW</div>
              <div class="brand-sub">Gateway Dashboard</div>
            </div>
          </div>
        </div>
        <div class="topbar-status">
          <div class="pill">
            <span class="statusDot ${versionStatusClass}"></span>
            <span>${t("common.version")}</span>
            <span class="mono">${openClawVersion}</span>
          </div>
          <div class="pill">
            <span class="statusDot ${state.connected ? "ok" : ""}"></span>
            <span>${t("common.health")}</span>
            <span class="mono">${state.connected ? t("common.ok") : t("common.offline")}</span>
          </div>
          ${renderThemeToggle(state)}
        </div>
      </header>
      <aside class="nav ${state.settings.navCollapsed ? "nav--collapsed" : ""}">
        ${TAB_GROUPS.map((group) => {
          const isGroupCollapsed = state.settings.navGroupsCollapsed[group.label] ?? false;
          const hasActiveTab = group.tabs.some((tab) => tab === state.tab);
          return html`
            <div class="nav-group ${isGroupCollapsed && !hasActiveTab ? "nav-group--collapsed" : ""}">
              <button
                class="nav-label"
                @click=${() => {
                  const next = { ...state.settings.navGroupsCollapsed };
                  next[group.label] = !isGroupCollapsed;
                  state.applySettings({
                    ...state.settings,
                    navGroupsCollapsed: next,
                  });
                }}
                aria-expanded=${!isGroupCollapsed}
              >
                <span class="nav-label__text">${t(`nav.${group.label}`)}</span>
                <span class="nav-label__chevron">${isGroupCollapsed ? "+" : "−"}</span>
              </button>
              <div class="nav-group__items">
                ${group.tabs.map((tab) => renderTab(state, tab))}
              </div>
            </div>
          `;
        })}
        <div class="nav-group nav-group--links">
          <div class="nav-label nav-label--static">
            <span class="nav-label__text">${t("common.resources")}</span>
          </div>
          <div class="nav-group__items">
            <a
              class="nav-item nav-item--external"
              href="https://docs.openclaw.ai"
              target=${EXTERNAL_LINK_TARGET}
              rel=${buildExternalLinkRel()}
              title="${t("common.docs")} (opens in new tab)"
            >
              <span class="nav-item__icon" aria-hidden="true">${icons.book}</span>
              <span class="nav-item__text">${t("common.docs")}</span>
            </a>
          </div>
        </div>
      </aside>
      <main class="content ${isChat ? "content--chat" : ""}">
        ${
          availableUpdate
            ? html`<div class="update-banner callout danger" role="alert">
              <strong>Update available:</strong> v${availableUpdate.latestVersion}
              (running v${availableUpdate.currentVersion}).
              <button
                class="btn btn--sm update-banner__btn"
                ?disabled=${state.updateRunning || !state.connected}
                @click=${() => runUpdate(state)}
              >${state.updateRunning ? "Updating…" : "Update now"}</button>
            </div>`
            : nothing
        }
        <section class="content-header">
          <div>
            ${state.tab === "usage" ? nothing : html`<div class="page-title">${titleForTab(state.tab)}</div>`}
            ${state.tab === "usage" ? nothing : html`<div class="page-sub">${subtitleForTab(state.tab)}</div>`}
          </div>
          <div class="page-meta">
            ${state.lastError ? html`<div class="pill danger">${state.lastError}</div>` : nothing}
            ${isChat ? renderChatControls(state) : nothing}
          </div>
        </section>

        ${
          state.tab === "overview"
            ? renderOverview({
                connected: state.connected,
                hello: state.hello,
                settings: state.settings,
                password: state.password,
                lastError: state.lastError,
                lastErrorCode: state.lastErrorCode,
                presenceCount,
                sessionsCount,
                cronEnabled: state.cronStatus?.enabled ?? null,
                cronNext,
                lastChannelsRefresh: state.channelsLastSuccess,
                onSettingsChange: (next) => state.applySettings(next),
                onPasswordChange: (next) => (state.password = next),
                onSessionKeyChange: (next) => {
                  state.sessionKey = next;
                  state.chatMessage = "";
                  state.resetToolStream();
                  state.applySettings({
                    ...state.settings,
                    sessionKey: next,
                    lastActiveSessionKey: next,
                  });
                  void state.loadAssistantIdentity();
                },
                onConnect: () => state.connect(),
                onRefresh: () => state.loadOverview(),
              })
            : nothing
        }

        ${
          state.tab === "channels"
            ? renderChannels({
                connected: state.connected,
                loading: state.channelsLoading,
                snapshot: state.channelsSnapshot,
                lastError: state.channelsError,
                lastSuccessAt: state.channelsLastSuccess,
                whatsappMessage: state.whatsappLoginMessage,
                whatsappQrDataUrl: state.whatsappLoginQrDataUrl,
                whatsappConnected: state.whatsappLoginConnected,
                whatsappBusy: state.whatsappBusy,
                configSchema: state.configSchema,
                configSchemaLoading: state.configSchemaLoading,
                configForm: state.configForm,
                configUiHints: state.configUiHints,
                configSaving: state.configSaving,
                configFormDirty: state.configFormDirty,
                nostrProfileFormState: state.nostrProfileFormState,
                nostrProfileAccountId: state.nostrProfileAccountId,
                onRefresh: (probe) => loadChannels(state, probe),
                onWhatsAppStart: (force) => state.handleWhatsAppStart(force),
                onWhatsAppWait: () => state.handleWhatsAppWait(),
                onWhatsAppLogout: () => state.handleWhatsAppLogout(),
                onConfigPatch: (path, value) => updateConfigFormValue(state, path, value),
                onConfigSave: () => state.handleChannelConfigSave(),
                onConfigReload: () => state.handleChannelConfigReload(),
                onNostrProfileEdit: (accountId, profile) =>
                  state.handleNostrProfileEdit(accountId, profile),
                onNostrProfileCancel: () => state.handleNostrProfileCancel(),
                onNostrProfileFieldChange: (field, value) =>
                  state.handleNostrProfileFieldChange(field, value),
                onNostrProfileSave: () => state.handleNostrProfileSave(),
                onNostrProfileImport: () => state.handleNostrProfileImport(),
                onNostrProfileToggleAdvanced: () => state.handleNostrProfileToggleAdvanced(),
              })
            : nothing
        }

        ${
          state.tab === "instances"
            ? renderInstances({
                loading: state.presenceLoading,
                entries: state.presenceEntries,
                lastError: state.presenceError,
                statusMessage: state.presenceStatus,
                onRefresh: () => loadPresence(state),
              })
            : nothing
        }

        ${
          state.tab === "sessions"
            ? renderSessions({
                loading: state.sessionsLoading,
                result: state.sessionsResult,
                error: state.sessionsError,
                activeMinutes: state.sessionsFilterActive,
                limit: state.sessionsFilterLimit,
                includeGlobal: state.sessionsIncludeGlobal,
                includeUnknown: state.sessionsIncludeUnknown,
                basePath: state.basePath,
                onFiltersChange: (next) => {
                  state.sessionsFilterActive = next.activeMinutes;
                  state.sessionsFilterLimit = next.limit;
                  state.sessionsIncludeGlobal = next.includeGlobal;
                  state.sessionsIncludeUnknown = next.includeUnknown;
                },
                onRefresh: () => loadSessions(state),
                onPatch: (key, patch) => patchSession(state, key, patch),
                onDelete: (key) => deleteSessionAndRefresh(state, key),
              })
            : nothing
        }

        ${renderUsageTab(state)}

        ${
          state.tab === "cron"
            ? renderCron({
                basePath: state.basePath,
                loading: state.cronLoading,
                jobsLoadingMore: state.cronJobsLoadingMore,
                status: state.cronStatus,
                jobs: visibleCronJobs,
                jobsTotal: state.cronJobsTotal,
                jobsHasMore: state.cronJobsHasMore,
                jobsQuery: state.cronJobsQuery,
                jobsEnabledFilter: state.cronJobsEnabledFilter,
                jobsScheduleKindFilter: state.cronJobsScheduleKindFilter,
                jobsLastStatusFilter: state.cronJobsLastStatusFilter,
                jobsSortBy: state.cronJobsSortBy,
                jobsSortDir: state.cronJobsSortDir,
                error: state.cronError,
                busy: state.cronBusy,
                form: state.cronForm,
                fieldErrors: state.cronFieldErrors,
                canSubmit: !hasCronFormErrors(state.cronFieldErrors),
                editingJobId: state.cronEditingJobId,
                channels: state.channelsSnapshot?.channelMeta?.length
                  ? state.channelsSnapshot.channelMeta.map((entry) => entry.id)
                  : (state.channelsSnapshot?.channelOrder ?? []),
                channelLabels: state.channelsSnapshot?.channelLabels ?? {},
                channelMeta: state.channelsSnapshot?.channelMeta ?? [],
                runsJobId: state.cronRunsJobId,
                runs: state.cronRuns,
                runsTotal: state.cronRunsTotal,
                runsHasMore: state.cronRunsHasMore,
                runsLoadingMore: state.cronRunsLoadingMore,
                runsScope: state.cronRunsScope,
                runsStatuses: state.cronRunsStatuses,
                runsDeliveryStatuses: state.cronRunsDeliveryStatuses,
                runsStatusFilter: state.cronRunsStatusFilter,
                runsQuery: state.cronRunsQuery,
                runsSortDir: state.cronRunsSortDir,
                agentSuggestions: cronAgentSuggestions,
                modelSuggestions: cronModelSuggestions,
                thinkingSuggestions: CRON_THINKING_SUGGESTIONS,
                timezoneSuggestions: CRON_TIMEZONE_SUGGESTIONS,
                deliveryToSuggestions,
                onFormChange: (patch) => {
                  state.cronForm = normalizeCronFormState({ ...state.cronForm, ...patch });
                  state.cronFieldErrors = validateCronForm(state.cronForm);
                },
                onRefresh: () => state.loadCron(),
                onAdd: () => addCronJob(state),
                onEdit: (job) => startCronEdit(state, job),
                onClone: (job) => startCronClone(state, job),
                onCancelEdit: () => cancelCronEdit(state),
                onToggle: (job, enabled) => toggleCronJob(state, job, enabled),
                onRun: (job) => runCronJob(state, job),
                onRemove: (job) => removeCronJob(state, job),
                onLoadRuns: async (jobId) => {
                  updateCronRunsFilter(state, { cronRunsScope: "job" });
                  await loadCronRuns(state, jobId);
                },
                onLoadMoreJobs: () => loadMoreCronJobs(state),
                onJobsFiltersChange: async (patch) => {
                  updateCronJobsFilter(state, patch);
                  const shouldReload =
                    typeof patch.cronJobsQuery === "string" ||
                    Boolean(patch.cronJobsEnabledFilter) ||
                    Boolean(patch.cronJobsSortBy) ||
                    Boolean(patch.cronJobsSortDir);
                  if (shouldReload) {
                    await reloadCronJobs(state);
                  }
                },
                onJobsFiltersReset: async () => {
                  updateCronJobsFilter(state, {
                    cronJobsQuery: "",
                    cronJobsEnabledFilter: "all",
                    cronJobsScheduleKindFilter: "all",
                    cronJobsLastStatusFilter: "all",
                    cronJobsSortBy: "nextRunAtMs",
                    cronJobsSortDir: "asc",
                  });
                  await reloadCronJobs(state);
                },
                onLoadMoreRuns: () => loadMoreCronRuns(state),
                onRunsFiltersChange: async (patch) => {
                  updateCronRunsFilter(state, patch);
                  if (state.cronRunsScope === "all") {
                    await loadCronRuns(state, null);
                    return;
                  }
                  await loadCronRuns(state, state.cronRunsJobId);
                },
              })
            : nothing
        }

        ${
          state.tab === "agents"
            ? renderAgents({
                loading: state.agentsLoading,
                error: state.agentsError,
                agentsList: state.agentsList,
                selectedAgentId: resolvedAgentId,
                activePanel: state.agentsPanel,
                configForm: configValue,
                configLoading: state.configLoading,
                configSaving: state.configSaving,
                configDirty: state.configFormDirty,
                channelsLoading: state.channelsLoading,
                channelsError: state.channelsError,
                channelsSnapshot: state.channelsSnapshot,
                channelsLastSuccess: state.channelsLastSuccess,
                cronLoading: state.cronLoading,
                cronStatus: state.cronStatus,
                cronJobs: state.cronJobs,
                cronError: state.cronError,
                agentFilesLoading: state.agentFilesLoading,
                agentFilesError: state.agentFilesError,
                agentFilesList: state.agentFilesList,
                agentFileActive: state.agentFileActive,
                agentFileContents: state.agentFileContents,
                agentFileDrafts: state.agentFileDrafts,
                agentFileSaving: state.agentFileSaving,
                agentIdentityLoading: state.agentIdentityLoading,
                agentIdentityError: state.agentIdentityError,
                agentIdentityById: state.agentIdentityById,
                agentSkillsLoading: state.agentSkillsLoading,
                agentSkillsReport: state.agentSkillsReport,
                agentSkillsError: state.agentSkillsError,
                agentSkillsAgentId: state.agentSkillsAgentId,
                toolsCatalogLoading: state.toolsCatalogLoading,
                toolsCatalogError: state.toolsCatalogError,
                toolsCatalogResult: state.toolsCatalogResult,
                skillsFilter: state.skillsFilter,
                onRefresh: async () => {
                  await loadAgents(state);
                  const nextSelected =
                    state.agentsSelectedId ??
                    state.agentsList?.defaultId ??
                    state.agentsList?.agents?.[0]?.id ??
                    null;
                  await loadToolsCatalog(state, nextSelected);
                  const agentIds = state.agentsList?.agents?.map((entry) => entry.id) ?? [];
                  if (agentIds.length > 0) {
                    void loadAgentIdentities(state, agentIds);
                  }
                },
                onSelectAgent: (agentId) => {
                  if (state.agentsSelectedId === agentId) {
                    return;
                  }
                  state.agentsSelectedId = agentId;
                  state.agentFilesList = null;
                  state.agentFilesError = null;
                  state.agentFilesLoading = false;
                  state.agentFileActive = null;
                  state.agentFileContents = {};
                  state.agentFileDrafts = {};
                  state.agentSkillsReport = null;
                  state.agentSkillsError = null;
                  state.agentSkillsAgentId = null;
                  void loadAgentIdentity(state, agentId);
                  if (state.agentsPanel === "tools") {
                    void loadToolsCatalog(state, agentId);
                  }
                  if (state.agentsPanel === "files") {
                    void loadAgentFiles(state, agentId);
                  }
                  if (state.agentsPanel === "skills") {
                    void loadAgentSkills(state, agentId);
                  }
                },
                onSelectPanel: (panel) => {
                  state.agentsPanel = panel;
                  if (panel === "files" && resolvedAgentId) {
                    if (state.agentFilesList?.agentId !== resolvedAgentId) {
                      state.agentFilesList = null;
                      state.agentFilesError = null;
                      state.agentFileActive = null;
                      state.agentFileContents = {};
                      state.agentFileDrafts = {};
                      void loadAgentFiles(state, resolvedAgentId);
                    }
                  }
                  if (panel === "tools") {
                    void loadToolsCatalog(state, resolvedAgentId);
                  }
                  if (panel === "skills") {
                    if (resolvedAgentId) {
                      void loadAgentSkills(state, resolvedAgentId);
                    }
                  }
                  if (panel === "channels") {
                    void loadChannels(state, false);
                  }
                  if (panel === "cron") {
                    void state.loadCron();
                  }
                },
                onLoadFiles: (agentId) => loadAgentFiles(state, agentId),
                onSelectFile: (name) => {
                  state.agentFileActive = name;
                  if (!resolvedAgentId) {
                    return;
                  }
                  void loadAgentFileContent(state, resolvedAgentId, name);
                },
                onFileDraftChange: (name, content) => {
                  state.agentFileDrafts = { ...state.agentFileDrafts, [name]: content };
                },
                onFileReset: (name) => {
                  const base = state.agentFileContents[name] ?? "";
                  state.agentFileDrafts = { ...state.agentFileDrafts, [name]: base };
                },
                onFileSave: (name) => {
                  if (!resolvedAgentId) {
                    return;
                  }
                  const content =
                    state.agentFileDrafts[name] ?? state.agentFileContents[name] ?? "";
                  void saveAgentFile(state, resolvedAgentId, name, content);
                },
                onToolsProfileChange: (agentId, profile, clearAllow) => {
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  const basePath = ["agents", "list", index, "tools"];
                  if (profile) {
                    updateConfigFormValue(state, [...basePath, "profile"], profile);
                  } else {
                    removeConfigFormValue(state, [...basePath, "profile"]);
                  }
                  if (clearAllow) {
                    removeConfigFormValue(state, [...basePath, "allow"]);
                  }
                },
                onToolsOverridesChange: (agentId, alsoAllow, deny) => {
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  const basePath = ["agents", "list", index, "tools"];
                  if (alsoAllow.length > 0) {
                    updateConfigFormValue(state, [...basePath, "alsoAllow"], alsoAllow);
                  } else {
                    removeConfigFormValue(state, [...basePath, "alsoAllow"]);
                  }
                  if (deny.length > 0) {
                    updateConfigFormValue(state, [...basePath, "deny"], deny);
                  } else {
                    removeConfigFormValue(state, [...basePath, "deny"]);
                  }
                },
                onConfigReload: () => loadConfig(state),
                onConfigSave: () => saveConfig(state),
                onChannelsRefresh: () => loadChannels(state, false),
                onCronRefresh: () => state.loadCron(),
                onSkillsFilterChange: (next) => (state.skillsFilter = next),
                onSkillsRefresh: () => {
                  if (resolvedAgentId) {
                    void loadAgentSkills(state, resolvedAgentId);
                  }
                },
                onAgentSkillToggle: (agentId, skillName, enabled) => {
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  const entry = list[index] as { skills?: unknown };
                  const normalizedSkill = skillName.trim();
                  if (!normalizedSkill) {
                    return;
                  }
                  const allSkills =
                    state.agentSkillsReport?.skills?.map((skill) => skill.name).filter(Boolean) ??
                    [];
                  const existing = Array.isArray(entry.skills)
                    ? entry.skills.map((name) => String(name).trim()).filter(Boolean)
                    : undefined;
                  const base = existing ?? allSkills;
                  const next = new Set(base);
                  if (enabled) {
                    next.add(normalizedSkill);
                  } else {
                    next.delete(normalizedSkill);
                  }
                  updateConfigFormValue(state, ["agents", "list", index, "skills"], [...next]);
                },
                onAgentSkillsClear: (agentId) => {
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  removeConfigFormValue(state, ["agents", "list", index, "skills"]);
                },
                onAgentSkillsDisableAll: (agentId) => {
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  updateConfigFormValue(state, ["agents", "list", index, "skills"], []);
                },
                onModelChange: (agentId, modelId) => {
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  const basePath = ["agents", "list", index, "model"];
                  if (!modelId) {
                    removeConfigFormValue(state, basePath);
                    return;
                  }
                  const entry = list[index] as { model?: unknown };
                  const existing = entry?.model;
                  if (existing && typeof existing === "object" && !Array.isArray(existing)) {
                    const fallbacks = (existing as { fallbacks?: unknown }).fallbacks;
                    const next = {
                      primary: modelId,
                      ...(Array.isArray(fallbacks) ? { fallbacks } : {}),
                    };
                    updateConfigFormValue(state, basePath, next);
                  } else {
                    updateConfigFormValue(state, basePath, modelId);
                  }
                },
                onModelFallbacksChange: (agentId, fallbacks) => {
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  const basePath = ["agents", "list", index, "model"];
                  const entry = list[index] as { model?: unknown };
                  const normalized = fallbacks.map((name) => name.trim()).filter(Boolean);
                  const existing = entry.model;
                  const resolvePrimary = () => {
                    if (typeof existing === "string") {
                      return existing.trim() || null;
                    }
                    if (existing && typeof existing === "object" && !Array.isArray(existing)) {
                      const primary = (existing as { primary?: unknown }).primary;
                      if (typeof primary === "string") {
                        const trimmed = primary.trim();
                        return trimmed || null;
                      }
                    }
                    return null;
                  };
                  const primary = resolvePrimary();
                  if (normalized.length === 0) {
                    if (primary) {
                      updateConfigFormValue(state, basePath, primary);
                    } else {
                      removeConfigFormValue(state, basePath);
                    }
                    return;
                  }
                  const next = primary
                    ? { primary, fallbacks: normalized }
                    : { fallbacks: normalized };
                  updateConfigFormValue(state, basePath, next);
                },
              })
            : nothing
        }

        ${
          state.tab === "skills"
            ? renderSkills({
                loading: state.skillsLoading,
                report: state.skillsReport,
                error: state.skillsError,
                filter: state.skillsFilter,
                edits: state.skillEdits,
                messages: state.skillMessages,
                busyKey: state.skillsBusyKey,
                onFilterChange: (next) => (state.skillsFilter = next),
                onRefresh: () => loadSkills(state, { clearMessages: true }),
                onToggle: (key, enabled) => updateSkillEnabled(state, key, enabled),
                onEdit: (key, value) => updateSkillEdit(state, key, value),
                onSaveKey: (key) => saveSkillApiKey(state, key),
                onInstall: (skillKey, name, installId) =>
                  installSkill(state, skillKey, name, installId),
              })
            : nothing
        }

        ${
          state.tab === "nodes"
            ? renderNodes({
                loading: state.nodesLoading,
                nodes: state.nodes,
                devicesLoading: state.devicesLoading,
                devicesError: state.devicesError,
                devicesList: state.devicesList,
                configForm:
                  state.configForm ??
                  (state.configSnapshot?.config as Record<string, unknown> | null),
                configLoading: state.configLoading,
                configSaving: state.configSaving,
                configDirty: state.configFormDirty,
                configFormMode: state.configFormMode,
                execApprovalsLoading: state.execApprovalsLoading,
                execApprovalsSaving: state.execApprovalsSaving,
                execApprovalsDirty: state.execApprovalsDirty,
                execApprovalsSnapshot: state.execApprovalsSnapshot,
                execApprovalsForm: state.execApprovalsForm,
                execApprovalsSelectedAgent: state.execApprovalsSelectedAgent,
                execApprovalsTarget: state.execApprovalsTarget,
                execApprovalsTargetNodeId: state.execApprovalsTargetNodeId,
                onRefresh: () => loadNodes(state),
                onDevicesRefresh: () => loadDevices(state),
                onDeviceApprove: (requestId) => approveDevicePairing(state, requestId),
                onDeviceReject: (requestId) => rejectDevicePairing(state, requestId),
                onDeviceRotate: (deviceId, role, scopes) =>
                  rotateDeviceToken(state, { deviceId, role, scopes }),
                onDeviceRevoke: (deviceId, role) => revokeDeviceToken(state, { deviceId, role }),
                onLoadConfig: () => loadConfig(state),
                onLoadExecApprovals: () => {
                  const target =
                    state.execApprovalsTarget === "node" && state.execApprovalsTargetNodeId
                      ? { kind: "node" as const, nodeId: state.execApprovalsTargetNodeId }
                      : { kind: "gateway" as const };
                  return loadExecApprovals(state, target);
                },
                onBindDefault: (nodeId) => {
                  if (nodeId) {
                    updateConfigFormValue(state, ["tools", "exec", "node"], nodeId);
                  } else {
                    removeConfigFormValue(state, ["tools", "exec", "node"]);
                  }
                },
                onBindAgent: (agentIndex, nodeId) => {
                  const basePath = ["agents", "list", agentIndex, "tools", "exec", "node"];
                  if (nodeId) {
                    updateConfigFormValue(state, basePath, nodeId);
                  } else {
                    removeConfigFormValue(state, basePath);
                  }
                },
                onSaveBindings: () => saveConfig(state),
                onExecApprovalsTargetChange: (kind, nodeId) => {
                  state.execApprovalsTarget = kind;
                  state.execApprovalsTargetNodeId = nodeId;
                  state.execApprovalsSnapshot = null;
                  state.execApprovalsForm = null;
                  state.execApprovalsDirty = false;
                  state.execApprovalsSelectedAgent = null;
                },
                onExecApprovalsSelectAgent: (agentId) => {
                  state.execApprovalsSelectedAgent = agentId;
                },
                onExecApprovalsPatch: (path, value) =>
                  updateExecApprovalsFormValue(state, path, value),
                onExecApprovalsRemove: (path) => removeExecApprovalsFormValue(state, path),
                onSaveExecApprovals: () => {
                  const target =
                    state.execApprovalsTarget === "node" && state.execApprovalsTargetNodeId
                      ? { kind: "node" as const, nodeId: state.execApprovalsTargetNodeId }
                      : { kind: "gateway" as const };
                  return saveExecApprovals(state, target);
                },
              })
            : nothing
        }

        ${
          state.tab === "contentforge"
            ? renderContentForge({
                loading: state.contentforgeLoading,
                error: state.contentforgeError,
                dashboard: state.contentforgeDashboard,
                subView: state.contentforgeSubView,
                onSubViewChange: (view) => {
                  state.contentforgeSubView = view;
                },
                onRefresh: () => state.loadContentForge(),
                client: state.client,
              })
            : nothing
        }

        ${
          state.tab === "chat"
            ? renderChat({
                sessionKey: state.sessionKey,
                onSessionKeyChange: (next) => {
                  state.sessionKey = next;
                  state.chatMessage = "";
                  state.chatAttachments = [];
                  state.chatStream = null;
                  state.chatStreamStartedAt = null;
                  state.chatRunId = null;
                  state.chatQueue = [];
                  state.resetToolStream();
                  state.resetChatScroll();
                  state.applySettings({
                    ...state.settings,
                    sessionKey: next,
                    lastActiveSessionKey: next,
                  });
                  void state.loadAssistantIdentity();
                  void loadChatHistory(state);
                  void refreshChatAvatar(state);
                },
                thinkingLevel: state.chatThinkingLevel,
                showThinking,
                loading: state.chatLoading,
                sending: state.chatSending,
                compactionStatus: state.compactionStatus,
                fallbackStatus: state.fallbackStatus,
                assistantAvatarUrl: chatAvatarUrl,
                messages: state.chatMessages,
                toolMessages: state.chatToolMessages,
                stream: state.chatStream,
                streamStartedAt: state.chatStreamStartedAt,
                draft: state.chatMessage,
                queue: state.chatQueue,
                connected: state.connected,
                canSend: state.connected,
                disabledReason: chatDisabledReason,
                error: state.lastError,
                sessions: state.sessionsResult,
                focusMode: chatFocus,
                onRefresh: () => {
                  state.resetToolStream();
                  return Promise.all([loadChatHistory(state), refreshChatAvatar(state)]);
                },
                onToggleFocusMode: () => {
                  if (state.onboarding) {
                    return;
                  }
                  state.applySettings({
                    ...state.settings,
                    chatFocusMode: !state.settings.chatFocusMode,
                  });
                },
                onChatScroll: (event) => state.handleChatScroll(event),
                onDraftChange: (next) => (state.chatMessage = next),
                attachments: state.chatAttachments,
                onAttachmentsChange: (next) => (state.chatAttachments = next),
                onSend: () => state.handleSendChat(),
                canAbort: Boolean(state.chatRunId),
                onAbort: () => void state.handleAbortChat(),
                onQueueRemove: (id) => state.removeQueuedMessage(id),
                onNewSession: () => state.handleSendChat("/new", { restoreDraft: true }),
                showNewMessages: state.chatNewMessagesBelow && !state.chatManualRefreshInFlight,
                onScrollToBottom: () => state.scrollToBottom(),
                // Sidebar props for tool output viewing
                sidebarOpen: state.sidebarOpen,
                sidebarContent: state.sidebarContent,
                sidebarError: state.sidebarError,
                splitRatio: state.splitRatio,
                onOpenSidebar: (content: string) => state.handleOpenSidebar(content),
                onCloseSidebar: () => state.handleCloseSidebar(),
                onSplitRatioChange: (ratio: number) => state.handleSplitRatioChange(ratio),
                assistantName: state.assistantName,
                assistantAvatar: state.assistantAvatar,
              })
            : nothing
        }

        ${
          state.tab === "config"
            ? renderConfig({
                raw: state.configRaw,
                originalRaw: state.configRawOriginal,
                valid: state.configValid,
                issues: state.configIssues,
                loading: state.configLoading,
                saving: state.configSaving,
                applying: state.configApplying,
                updating: state.updateRunning,
                connected: state.connected,
                schema: state.configSchema,
                schemaLoading: state.configSchemaLoading,
                uiHints: state.configUiHints,
                formMode: state.configFormMode,
                formValue: state.configForm,
                originalValue: state.configFormOriginal,
                searchQuery: state.configSearchQuery,
                activeSection: state.configActiveSection,
                activeSubsection: state.configActiveSubsection,
                onRawChange: (next) => {
                  state.configRaw = next;
                },
                onFormModeChange: (mode) => (state.configFormMode = mode),
                onFormPatch: (path, value) => updateConfigFormValue(state, path, value),
                onSearchChange: (query) => (state.configSearchQuery = query),
                onSectionChange: (section) => {
                  state.configActiveSection = section;
                  state.configActiveSubsection = null;
                },
                onSubsectionChange: (section) => (state.configActiveSubsection = section),
                onReload: () => loadConfig(state),
                onSave: () => saveConfig(state),
                onApply: () => applyConfig(state),
                onUpdate: () => runUpdate(state),
              })
            : nothing
        }

        ${
          state.tab === "debug"
            ? renderDebug({
                loading: state.debugLoading,
                status: state.debugStatus,
                health: state.debugHealth,
                models: state.debugModels,
                heartbeat: state.debugHeartbeat,
                eventLog: state.eventLog,
                callMethod: state.debugCallMethod,
                callParams: state.debugCallParams,
                callResult: state.debugCallResult,
                callError: state.debugCallError,
                onCallMethodChange: (next) => (state.debugCallMethod = next),
                onCallParamsChange: (next) => (state.debugCallParams = next),
                onRefresh: () => loadDebug(state),
                onCall: () => callDebugMethod(state),
              })
            : nothing
        }

        ${
          state.tab === "logs"
            ? renderLogs({
                loading: state.logsLoading,
                error: state.logsError,
                file: state.logsFile,
                entries: state.logsEntries,
                filterText: state.logsFilterText,
                levelFilters: state.logsLevelFilters,
                autoFollow: state.logsAutoFollow,
                truncated: state.logsTruncated,
                onFilterTextChange: (next) => (state.logsFilterText = next),
                onLevelToggle: (level, enabled) => {
                  state.logsLevelFilters = { ...state.logsLevelFilters, [level]: enabled };
                },
                onToggleAutoFollow: (next) => (state.logsAutoFollow = next),
                onRefresh: () => loadLogs(state, { reset: true }),
                onExport: (lines, label) => state.exportLogs(lines, label),
                onScroll: (event) => state.handleLogsScroll(event),
              })
            : nothing
        }
      </main>
      ${renderExecApprovalPrompt(state)}
      ${renderGatewayUrlConfirmation(state)}
    </div>
  `;
}

// ═══════════════════════════════════════════════════════════════════════════
// Mission Control — Full Page Layout
// ═══════════════════════════════════════════════════════════════════════════

// ── Project Switcher ──────────────────────────────────────────────────

async function loadCfSites(state: AppViewState) {
  if (!state.client || !state.connected || state.cfSitesLoaded) {
    return;
  }
  try {
    const res = await state.client.request<{
      sites: Array<{ id: string; name: string; url: string }>;
    }>("contentforge.sites.list", {});
    state.cfSites = res?.sites ?? [];
    state.cfSitesLoaded = true;
    // Auto-select first site if none is active
    if (!state.activeSiteId && state.cfSites.length > 0) {
      state.activeSiteId = state.cfSites[0].id;
      localStorage.setItem("cf_activeSiteId", state.cfSites[0].id);
    }
  } catch (err) {
    console.warn("[CF] Failed to load sites:", err);
  }
}

function selectSite(state: AppViewState, siteId: string | null) {
  state.activeSiteId = siteId;
  state.projectSwitcherOpen = false;
  if (siteId) {
    localStorage.setItem("cf_activeSiteId", siteId);
  } else {
    localStorage.removeItem("cf_activeSiteId");
  }
  // Clear cached data so it reloads for the new project
  state.cfApprovals = [];
  state.cfKeywords = [] as unknown as typeof state.cfKeywords;
  (state as Record<string, unknown>).cfCreatingProject = false;
  (state as Record<string, unknown>)._approvalsLoadedForSiteId = null;
  // Reload dashboard for the new site context
  void state.loadContentForge();
}

function renderProjectSwitcher(state: AppViewState) {
  // Lazy-load sites on first render
  if (!state.cfSitesLoaded && state.connected) {
    void loadCfSites(state);
  }

  const activeSite = state.cfSites.find((s) => s.id === state.activeSiteId);
  const label = activeSite?.name || activeSite?.url || "Select Project";

  return html`
    <div class="mc-project-switcher">
      <button
        class="mc-project-switcher__trigger"
        @click=${(e: Event) => {
          e.stopPropagation();
          state.projectSwitcherOpen = !state.projectSwitcherOpen;
        }}
        title="Switch Project"
      >
        <span class="mc-project-switcher__icon">${icons.folder}</span>
        <span class="mc-project-switcher__label">${label}</span>
        <span class="mc-project-switcher__caret">▾</span>
      </button>
      ${
        state.projectSwitcherOpen
          ? html`
            <div class="mc-project-switcher__dropdown"
              @click=${(e: Event) => e.stopPropagation()}>
              <div class="mc-project-switcher__header">Projects</div>
              <button
                class="mc-project-switcher__item ${!state.activeSiteId ? "mc-project-switcher__item--active" : ""}"
                @click=${() => selectSite(state, null)}
              >
                <span>🌍</span> All Projects
              </button>
              ${state.cfSites.map(
                (site) => html`
                  <button
                    class="mc-project-switcher__item ${state.activeSiteId === site.id ? "mc-project-switcher__item--active" : ""}"
                    @click=${() => selectSite(state, site.id)}
                  >
                    <span>🌐</span>
                    <span class="mc-project-switcher__site-name">${site.name || site.url}</span>
                  </button>
                `,
              )}
              <div class="mc-project-switcher__divider"></div>
              <button
                class="mc-project-switcher__item mc-project-switcher__item--add"
                @click=${() => {
                  state.projectSwitcherOpen = false;
                  (state as Record<string, unknown>).cfCreatingProject = true;
                  state.setTab("mc-settings" as Tab);
                }}
              >
                <span>➕</span> Add Project
              </button>
            </div>
            <div class="mc-project-switcher__backdrop"
              @click=${() => {
                state.projectSwitcherOpen = false;
              }}></div>
          `
          : nothing
      }
    </div>
  `;
}

function renderMissionControl(state: AppViewState) {
  const wrappedTab = getWrappedTab(state.tab);

  return html`
    <div class="shell shell--mc">
      <div class="mc-layout">
        <!-- MC Header -->
        <header class="mc-header">
          <div class="mc-header__left">
            <button
              class="mc-header__toggle"
              @click=${() =>
                state.applySettings({
                  ...state.settings,
                  navCollapsed: !state.settings.navCollapsed,
                })}
              title="${state.settings.navCollapsed ? t("nav.expand") : t("nav.collapse")}"
              aria-label="${state.settings.navCollapsed ? t("nav.expand") : t("nav.collapse")}"
            >
              <span>${icons.menu}</span>
            </button>
            <div class="mc-header__brand">
              <span class="mc-header__brand-icon">${icons.zap}</span>
              <span>${t("nav.mc")}</span>
            </div>
          </div>
          <div class="mc-header__right">
            <button class="mc-header__search" title="Search (\u2318K)">
              ${icons.search}
              <span>Search</span>
              <span class="mc-header__search-shortcut">\u2318K</span>
            </button>
            ${renderProjectSwitcher(state)}
            <button
              class="mc-header__btn"
              title="Council"
              @click=${() => state.setTab("mc-council" as Tab)}
            >
              ${icons.messageSquare}
              <span>Council</span>
            </button>
            <span
              class="mc-header__health ${state.connected ? "mc-header__health--ok" : ""}"
              title="${state.connected ? "Connected" : "Disconnected"}"
            ></span>
            ${renderThemeToggle(state)}
          </div>
        </header>

        <!-- MC Sidebar -->
        <aside class="mc-sidebar ${!state.settings.navCollapsed ? "mc-sidebar--mobile-open" : ""}">
          <nav class="mc-sidebar__items">
            ${MC_SIDEBAR_ITEMS.map((item) => {
              if (item.dividerBefore) {
                return html`
                  <div class="mc-sidebar__divider"></div>
                  ${renderMcSidebarItem(state, item)}
                `;
              }
              return renderMcSidebarItem(state, item);
            })}
          </nav>
          <div class="mc-sidebar__footer">
            <button
              class="mc-sidebar__back"
              @click=${() => state.setTab("overview" as Tab)}
              title="${t("nav.backToOpenclaw")}"
            >
              ${t("nav.backToOpenclaw")}
            </button>
          </div>
        </aside>
        ${
          !state.settings.navCollapsed
            ? html`<div class="mc-sidebar-overlay" @click=${() =>
                state.applySettings({
                  ...state.settings,
                  navCollapsed: true,
                })}></div>`
            : nothing
        }

        <!-- MC Main Content -->
        <main class="mc-main ${wrappedTab ? "mc-main--wrapped" : "mc-main--native"}">
          ${renderMcContent(state, wrappedTab)}
        </main>
      </div>
      ${renderExecApprovalPrompt(state)}
      ${renderGatewayUrlConfirmation(state)}
    </div>
  `;
}

function renderMcSidebarItem(state: AppViewState, item: (typeof MC_SIDEBAR_ITEMS)[number]) {
  const isActive = state.tab === item.id;
  return html`
    <button
      class="mc-sidebar__item ${isActive ? "mc-sidebar__item--active" : ""}"
      @click=${() => {
        state.setTab(item.id);
        // Auto-collapse on mobile
        if (window.innerWidth <= 768 && !state.settings.navCollapsed) {
          state.applySettings({ ...state.settings, navCollapsed: true });
        }
      }}
      title=${item.label}
    >
      <span class="mc-sidebar__item-icon" aria-hidden="true">${icons[item.icon]}</span>
      <span class="mc-sidebar__item-label">${item.label}</span>
    </button>
  `;
}

function renderMcContent(state: AppViewState, wrappedTab: Tab | null) {
  const effectiveTab = wrappedTab ?? state.tab;

  // ── Council → Chat ──────────────────────────────────────────────
  if (effectiveTab === "chat") {
    const showThinking = state.onboarding ? false : state.settings.chatShowThinking;
    const assistantAvatarUrl = resolveAssistantAvatarUrl(state);
    const chatAvatarUrl = state.chatAvatarUrl ?? assistantAvatarUrl ?? null;
    const chatDisabledReason = state.connected ? null : t("chat.disconnected");
    return renderChat({
      sessionKey: state.sessionKey,
      onSessionKeyChange: (next) => {
        state.sessionKey = next;
        state.chatMessage = "";
        state.chatAttachments = [];
        state.chatStream = null;
        state.chatStreamStartedAt = null;
        state.chatRunId = null;
        state.chatQueue = [];
        state.resetToolStream();
        state.resetChatScroll();
        state.applySettings({
          ...state.settings,
          sessionKey: next,
          lastActiveSessionKey: next,
        });
        void state.loadAssistantIdentity();
        void loadChatHistory(state);
        void refreshChatAvatar(state);
      },
      thinkingLevel: state.chatThinkingLevel,
      showThinking,
      loading: state.chatLoading,
      sending: state.chatSending,
      compactionStatus: state.compactionStatus,
      fallbackStatus: state.fallbackStatus,
      assistantAvatarUrl: chatAvatarUrl,
      messages: state.chatMessages,
      toolMessages: state.chatToolMessages,
      stream: state.chatStream,
      streamStartedAt: state.chatStreamStartedAt,
      draft: state.chatMessage,
      queue: state.chatQueue,
      connected: state.connected,
      canSend: state.connected,
      disabledReason: chatDisabledReason,
      error: state.lastError,
      sessions: state.sessionsResult,
      focusMode: false,
      onRefresh: () => {
        state.resetToolStream();
        return Promise.all([loadChatHistory(state), refreshChatAvatar(state)]);
      },
      onToggleFocusMode: () => {},
      onChatScroll: (event) => state.handleChatScroll(event),
      onDraftChange: (next) => (state.chatMessage = next),
      attachments: state.chatAttachments,
      onAttachmentsChange: (next) => (state.chatAttachments = next),
      onSend: () => state.handleSendChat(),
      canAbort: Boolean(state.chatRunId),
      onAbort: () => void state.handleAbortChat(),
      onQueueRemove: (id) => state.removeQueuedMessage(id),
      onNewSession: () => state.handleSendChat("/new", { restoreDraft: true }),
      showNewMessages: state.chatNewMessagesBelow && !state.chatManualRefreshInFlight,
      onScrollToBottom: () => state.scrollToBottom(),
      sidebarOpen: state.sidebarOpen,
      sidebarContent: state.sidebarContent,
      sidebarError: state.sidebarError,
      splitRatio: state.splitRatio,
      onOpenSidebar: (content: string) => state.handleOpenSidebar(content),
      onCloseSidebar: () => state.handleCloseSidebar(),
      onSplitRatioChange: (ratio: number) => state.handleSplitRatioChange(ratio),
      assistantName: state.assistantName,
      assistantAvatar: state.assistantAvatar,
    });
  }

  // ── Calendar → Cron ─────────────────────────────────────────────
  if (effectiveTab === "cron") {
    const configValue =
      state.configForm ?? (state.configSnapshot?.config as Record<string, unknown> | null);
    const cronAgentSuggestions = Array.from(
      new Set(
        [
          ...(state.agentsList?.agents?.map((entry) => entry.id.trim()) ?? []),
          ...state.cronJobs
            .map((job) => (typeof job.agentId === "string" ? job.agentId.trim() : ""))
            .filter(Boolean),
        ].filter(Boolean),
      ),
    ).toSorted((a, b) => a.localeCompare(b));
    const cronModelSuggestions = Array.from(
      new Set(
        [
          ...state.cronModelSuggestions,
          ...resolveConfiguredCronModelSuggestions(configValue),
          ...state.cronJobs
            .map((job) => {
              if (job.payload.kind !== "agentTurn" || typeof job.payload.model !== "string") {
                return "";
              }
              return job.payload.model.trim();
            })
            .filter(Boolean),
        ].filter(Boolean),
      ),
    ).toSorted((a, b) => a.localeCompare(b));
    const visibleCronJobs = getVisibleCronJobs(state);
    const selectedDeliveryChannel =
      state.cronForm.deliveryChannel && state.cronForm.deliveryChannel.trim()
        ? state.cronForm.deliveryChannel.trim()
        : "last";
    const jobToSuggestions = state.cronJobs
      .map((job) => normalizeSuggestionValue(job.delivery?.to))
      .filter(Boolean);
    const accountToSuggestions = (
      selectedDeliveryChannel === "last"
        ? Object.values(state.channelsSnapshot?.channelAccounts ?? {}).flat()
        : (state.channelsSnapshot?.channelAccounts?.[selectedDeliveryChannel] ?? [])
    )
      .flatMap((account) => [
        normalizeSuggestionValue(account.accountId),
        normalizeSuggestionValue(account.name),
      ])
      .filter(Boolean);
    const rawDeliveryToSuggestions = uniquePreserveOrder([
      ...jobToSuggestions,
      ...accountToSuggestions,
    ]);
    const deliveryToSuggestions =
      state.cronForm.deliveryMode === "webhook"
        ? rawDeliveryToSuggestions.filter((value) => isHttpUrl(value))
        : rawDeliveryToSuggestions;
    return renderCron({
      basePath: state.basePath,
      loading: state.cronLoading,
      jobsLoadingMore: state.cronJobsLoadingMore,
      status: state.cronStatus,
      jobs: visibleCronJobs,
      jobsTotal: state.cronJobsTotal,
      jobsHasMore: state.cronJobsHasMore,
      jobsQuery: state.cronJobsQuery,
      jobsEnabledFilter: state.cronJobsEnabledFilter,
      jobsScheduleKindFilter: state.cronJobsScheduleKindFilter,
      jobsLastStatusFilter: state.cronJobsLastStatusFilter,
      jobsSortBy: state.cronJobsSortBy,
      jobsSortDir: state.cronJobsSortDir,
      error: state.cronError,
      busy: state.cronBusy,
      form: state.cronForm,
      fieldErrors: state.cronFieldErrors,
      canSubmit: !hasCronFormErrors(state.cronFieldErrors),
      editingJobId: state.cronEditingJobId,
      channels: state.channelsSnapshot?.channelMeta?.length
        ? state.channelsSnapshot.channelMeta.map((entry) => entry.id)
        : (state.channelsSnapshot?.channelOrder ?? []),
      channelLabels: state.channelsSnapshot?.channelLabels ?? {},
      channelMeta: state.channelsSnapshot?.channelMeta ?? [],
      runsJobId: state.cronRunsJobId,
      runs: state.cronRuns,
      runsTotal: state.cronRunsTotal,
      runsHasMore: state.cronRunsHasMore,
      runsLoadingMore: state.cronRunsLoadingMore,
      runsScope: state.cronRunsScope,
      runsStatuses: state.cronRunsStatuses,
      runsDeliveryStatuses: state.cronRunsDeliveryStatuses,
      runsStatusFilter: state.cronRunsStatusFilter,
      runsQuery: state.cronRunsQuery,
      runsSortDir: state.cronRunsSortDir,
      agentSuggestions: cronAgentSuggestions,
      modelSuggestions: cronModelSuggestions,
      thinkingSuggestions: CRON_THINKING_SUGGESTIONS,
      timezoneSuggestions: CRON_TIMEZONE_SUGGESTIONS,
      deliveryToSuggestions,
      onFormChange: (patch) => {
        state.cronForm = normalizeCronFormState({ ...state.cronForm, ...patch });
        state.cronFieldErrors = validateCronForm(state.cronForm);
      },
      onRefresh: () => state.loadCron(),
      onAdd: () => addCronJob(state),
      onEdit: (job) => startCronEdit(state, job),
      onClone: (job) => startCronClone(state, job),
      onCancelEdit: () => cancelCronEdit(state),
      onToggle: (job, enabled) => toggleCronJob(state, job, enabled),
      onRun: (job) => runCronJob(state, job),
      onRemove: (job) => removeCronJob(state, job),
      onLoadRuns: async (jobId) => {
        updateCronRunsFilter(state, { cronRunsScope: "job" });
        await loadCronRuns(state, jobId);
      },
      onLoadMoreJobs: () => loadMoreCronJobs(state),
      onJobsFiltersChange: async (patch) => {
        updateCronJobsFilter(state, patch);
        const shouldReload =
          typeof patch.cronJobsQuery === "string" ||
          Boolean(patch.cronJobsEnabledFilter) ||
          Boolean(patch.cronJobsSortBy) ||
          Boolean(patch.cronJobsSortDir);
        if (shouldReload) {
          await reloadCronJobs(state);
        }
      },
      onJobsFiltersReset: async () => {
        updateCronJobsFilter(state, {
          cronJobsQuery: "",
          cronJobsEnabledFilter: "all",
          cronJobsScheduleKindFilter: "all",
          cronJobsLastStatusFilter: "all",
          cronJobsSortBy: "nextRunAtMs",
          cronJobsSortDir: "asc",
        });
        await reloadCronJobs(state);
      },
      onLoadMoreRuns: () => loadMoreCronRuns(state),
      onRunsFiltersChange: async (patch) => {
        updateCronRunsFilter(state, patch);
        if (state.cronRunsScope === "all") {
          await loadCronRuns(state, null);
          return;
        }
        await loadCronRuns(state, state.cronRunsJobId);
      },
    });
  }

  // ── People → Channels ──────────────────────────────────────────
  if (effectiveTab === "channels") {
    return renderChannels({
      connected: state.connected,
      loading: state.channelsLoading,
      snapshot: state.channelsSnapshot,
      lastError: state.channelsError,
      lastSuccessAt: state.channelsLastSuccess,
      whatsappMessage: state.whatsappLoginMessage,
      whatsappQrDataUrl: state.whatsappLoginQrDataUrl,
      whatsappConnected: state.whatsappLoginConnected,
      whatsappBusy: state.whatsappBusy,
      configSchema: state.configSchema,
      configSchemaLoading: state.configSchemaLoading,
      configForm: state.configForm,
      configUiHints: state.configUiHints,
      configSaving: state.configSaving,
      configFormDirty: state.configFormDirty,
      nostrProfileFormState: state.nostrProfileFormState,
      nostrProfileAccountId: state.nostrProfileAccountId,
      onRefresh: (probe) => loadChannels(state, probe),
      onWhatsAppStart: (force) => state.handleWhatsAppStart(force),
      onWhatsAppWait: () => state.handleWhatsAppWait(),
      onWhatsAppLogout: () => state.handleWhatsAppLogout(),
      onConfigPatch: (path, value) => updateConfigFormValue(state, path, value),
      onConfigSave: () => state.handleChannelConfigSave(),
      onConfigReload: () => state.handleChannelConfigReload(),
      onNostrProfileEdit: (accountId, profile) => state.handleNostrProfileEdit(accountId, profile),
      onNostrProfileCancel: () => state.handleNostrProfileCancel(),
      onNostrProfileFieldChange: (field, value) =>
        state.handleNostrProfileFieldChange(field, value),
      onNostrProfileSave: () => state.handleNostrProfileSave(),
      onNostrProfileImport: () => state.handleNostrProfileImport(),
      onNostrProfileToggleAdvanced: () => state.handleNostrProfileToggleAdvanced(),
    });
  }

  // ── Team → Agents ──────────────────────────────────────────────
  if (effectiveTab === "agents") {
    const configValue =
      state.configForm ?? (state.configSnapshot?.config as Record<string, unknown> | null);
    const resolvedAgentId =
      state.agentsSelectedId ??
      state.agentsList?.defaultId ??
      state.agentsList?.agents?.[0]?.id ??
      null;
    return renderAgents({
      loading: state.agentsLoading,
      error: state.agentsError,
      agentsList: state.agentsList,
      selectedAgentId: resolvedAgentId,
      activePanel: state.agentsPanel,
      configForm: configValue,
      configLoading: state.configLoading,
      configSaving: state.configSaving,
      configDirty: state.configFormDirty,
      channelsLoading: state.channelsLoading,
      channelsError: state.channelsError,
      channelsSnapshot: state.channelsSnapshot,
      channelsLastSuccess: state.channelsLastSuccess,
      cronLoading: state.cronLoading,
      cronStatus: state.cronStatus,
      cronJobs: state.cronJobs,
      cronError: state.cronError,
      agentFilesLoading: state.agentFilesLoading,
      agentFilesError: state.agentFilesError,
      agentFilesList: state.agentFilesList,
      agentFileActive: state.agentFileActive,
      agentFileContents: state.agentFileContents,
      agentFileDrafts: state.agentFileDrafts,
      agentFileSaving: state.agentFileSaving,
      agentIdentityLoading: state.agentIdentityLoading,
      agentIdentityError: state.agentIdentityError,
      agentIdentityById: state.agentIdentityById,
      agentSkillsLoading: state.agentSkillsLoading,
      agentSkillsReport: state.agentSkillsReport,
      agentSkillsError: state.agentSkillsError,
      agentSkillsAgentId: state.agentSkillsAgentId,
      toolsCatalogLoading: state.toolsCatalogLoading,
      toolsCatalogError: state.toolsCatalogError,
      toolsCatalogResult: state.toolsCatalogResult,
      skillsFilter: state.skillsFilter,
      onRefresh: async () => {
        await loadAgents(state);
        const nextSelected =
          state.agentsSelectedId ??
          state.agentsList?.defaultId ??
          state.agentsList?.agents?.[0]?.id ??
          null;
        await loadToolsCatalog(state, nextSelected);
        const agentIds = state.agentsList?.agents?.map((entry) => entry.id) ?? [];
        if (agentIds.length > 0) {
          void loadAgentIdentities(state, agentIds);
        }
      },
      onSelectAgent: (agentId) => {
        if (state.agentsSelectedId === agentId) {
          return;
        }
        state.agentsSelectedId = agentId;
        state.agentFilesList = null;
        state.agentFilesError = null;
        state.agentFilesLoading = false;
        state.agentFileActive = null;
        state.agentFileContents = {};
        state.agentFileDrafts = {};
        state.agentSkillsReport = null;
        state.agentSkillsError = null;
        state.agentSkillsAgentId = null;
        void loadAgentIdentity(state, agentId);
        if (state.agentsPanel === "tools") {
          void loadToolsCatalog(state, agentId);
        }
        if (state.agentsPanel === "files") {
          void loadAgentFiles(state, agentId);
        }
        if (state.agentsPanel === "skills") {
          void loadAgentSkills(state, agentId);
        }
      },
      onSelectPanel: (panel) => {
        state.agentsPanel = panel;
        if (panel === "files" && resolvedAgentId) {
          if (state.agentFilesList?.agentId !== resolvedAgentId) {
            state.agentFilesList = null;
            state.agentFilesError = null;
            state.agentFileActive = null;
            state.agentFileContents = {};
            state.agentFileDrafts = {};
            void loadAgentFiles(state, resolvedAgentId);
          }
        }
        if (panel === "tools") {
          void loadToolsCatalog(state, resolvedAgentId);
        }
        if (panel === "skills" && resolvedAgentId) {
          void loadAgentSkills(state, resolvedAgentId);
        }
        if (panel === "channels") {
          void loadChannels(state, false);
        }
        if (panel === "cron") {
          void state.loadCron();
        }
      },
      onLoadFiles: (agentId) => loadAgentFiles(state, agentId),
      onSelectFile: (name) => {
        state.agentFileActive = name;
        if (!resolvedAgentId) {
          return;
        }
        void loadAgentFileContent(state, resolvedAgentId, name);
      },
      onFileDraftChange: (name, content) => {
        state.agentFileDrafts = { ...state.agentFileDrafts, [name]: content };
      },
      onFileReset: (name) => {
        const base = state.agentFileContents[name] ?? "";
        state.agentFileDrafts = { ...state.agentFileDrafts, [name]: base };
      },
      onFileSave: (name) => {
        if (!resolvedAgentId) {
          return;
        }
        const content = state.agentFileDrafts[name] ?? state.agentFileContents[name] ?? "";
        void saveAgentFile(state, resolvedAgentId, name, content);
      },
      onToolsProfileChange: (agentId, profile, clearAllow) => {
        if (!configValue) {
          return;
        }
        const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
        if (!Array.isArray(list)) {
          return;
        }
        const index = list.findIndex(
          (entry) =>
            entry &&
            typeof entry === "object" &&
            "id" in entry &&
            (entry as { id?: string }).id === agentId,
        );
        if (index < 0) {
          return;
        }
        const basePath = ["agents", "list", index, "tools"];
        if (profile) {
          updateConfigFormValue(state, [...basePath, "profile"], profile);
        } else {
          removeConfigFormValue(state, [...basePath, "profile"]);
        }
        if (clearAllow) {
          removeConfigFormValue(state, [...basePath, "allow"]);
        }
      },
      onToolsOverridesChange: (agentId, alsoAllow, deny) => {
        if (!configValue) {
          return;
        }
        const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
        if (!Array.isArray(list)) {
          return;
        }
        const index = list.findIndex(
          (entry) =>
            entry &&
            typeof entry === "object" &&
            "id" in entry &&
            (entry as { id?: string }).id === agentId,
        );
        if (index < 0) {
          return;
        }
        const basePath = ["agents", "list", index, "tools"];
        if (alsoAllow.length > 0) {
          updateConfigFormValue(state, [...basePath, "alsoAllow"], alsoAllow);
        } else {
          removeConfigFormValue(state, [...basePath, "alsoAllow"]);
        }
        if (deny.length > 0) {
          updateConfigFormValue(state, [...basePath, "deny"], deny);
        } else {
          removeConfigFormValue(state, [...basePath, "deny"]);
        }
      },
      onConfigReload: () => loadConfig(state),
      onConfigSave: () => saveConfig(state),
      onChannelsRefresh: () => loadChannels(state, false),
      onCronRefresh: () => state.loadCron(),
      onSkillsFilterChange: (next) => (state.skillsFilter = next),
      onSkillsRefresh: () => {
        if (resolvedAgentId) {
          void loadAgentSkills(state, resolvedAgentId);
        }
      },
      onAgentSkillToggle: (agentId, skillName, enabled) => {
        if (!configValue) {
          return;
        }
        const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
        if (!Array.isArray(list)) {
          return;
        }
        const index = list.findIndex(
          (entry) =>
            entry &&
            typeof entry === "object" &&
            "id" in entry &&
            (entry as { id?: string }).id === agentId,
        );
        if (index < 0) {
          return;
        }
        const entry = list[index] as { skills?: unknown };
        const normalizedSkill = skillName.trim();
        if (!normalizedSkill) {
          return;
        }
        const allSkills =
          state.agentSkillsReport?.skills?.map((skill) => skill.name).filter(Boolean) ?? [];
        const existing = Array.isArray(entry.skills)
          ? entry.skills.map((name) => String(name).trim()).filter(Boolean)
          : undefined;
        const base = existing ?? allSkills;
        const next = new Set(base);
        if (enabled) {
          next.add(normalizedSkill);
        } else {
          next.delete(normalizedSkill);
        }
        updateConfigFormValue(state, ["agents", "list", index, "skills"], [...next]);
      },
      onAgentSkillsClear: (agentId) => {
        if (!configValue) {
          return;
        }
        const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
        if (!Array.isArray(list)) {
          return;
        }
        const index = list.findIndex(
          (entry) =>
            entry &&
            typeof entry === "object" &&
            "id" in entry &&
            (entry as { id?: string }).id === agentId,
        );
        if (index < 0) {
          return;
        }
        removeConfigFormValue(state, ["agents", "list", index, "skills"]);
      },
      onAgentSkillsDisableAll: (agentId) => {
        if (!configValue) {
          return;
        }
        const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
        if (!Array.isArray(list)) {
          return;
        }
        const index = list.findIndex(
          (entry) =>
            entry &&
            typeof entry === "object" &&
            "id" in entry &&
            (entry as { id?: string }).id === agentId,
        );
        if (index < 0) {
          return;
        }
        updateConfigFormValue(state, ["agents", "list", index, "skills"], []);
      },
      onModelChange: (agentId, modelId) => {
        if (!configValue) {
          return;
        }
        const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
        if (!Array.isArray(list)) {
          return;
        }
        const index = list.findIndex(
          (entry) =>
            entry &&
            typeof entry === "object" &&
            "id" in entry &&
            (entry as { id?: string }).id === agentId,
        );
        if (index < 0) {
          return;
        }
        const basePath = ["agents", "list", index, "model"];
        if (!modelId) {
          removeConfigFormValue(state, basePath);
          return;
        }
        const entryObj = list[index] as { model?: unknown };
        const existing = entryObj?.model;
        if (existing && typeof existing === "object" && !Array.isArray(existing)) {
          const fallbacks = (existing as { fallbacks?: unknown }).fallbacks;
          const next = { primary: modelId, ...(Array.isArray(fallbacks) ? { fallbacks } : {}) };
          updateConfigFormValue(state, basePath, next);
        } else {
          updateConfigFormValue(state, basePath, modelId);
        }
      },
      onModelFallbacksChange: (agentId, fallbacks) => {
        if (!configValue) {
          return;
        }
        const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
        if (!Array.isArray(list)) {
          return;
        }
        const index = list.findIndex(
          (entry) =>
            entry &&
            typeof entry === "object" &&
            "id" in entry &&
            (entry as { id?: string }).id === agentId,
        );
        if (index < 0) {
          return;
        }
        const basePath = ["agents", "list", index, "model"];
        const entryObj = list[index] as { model?: unknown };
        const normalized = fallbacks.map((name) => name.trim()).filter(Boolean);
        const existing = entryObj.model;
        const resolvePrimary = () => {
          if (typeof existing === "string") {
            return existing.trim() || null;
          }
          if (existing && typeof existing === "object" && !Array.isArray(existing)) {
            const primary = (existing as { primary?: unknown }).primary;
            if (typeof primary === "string") {
              return primary.trim() || null;
            }
          }
          return null;
        };
        const primary = resolvePrimary();
        if (normalized.length === 0) {
          if (primary) {
            updateConfigFormValue(state, basePath, primary);
          } else {
            removeConfigFormValue(state, basePath);
          }
          return;
        }
        const next = primary ? { primary, fallbacks: normalized } : { fallbacks: normalized };
        updateConfigFormValue(state, basePath, next);
      },
    });
  }

  // ── Usage ───────────────────────────────────────────────────────
  if (effectiveTab === "usage") {
    return renderUsageTab(state);
  }

  // ── Settings → Config ──────────────────────────────────────────
  if (effectiveTab === "config") {
    return renderConfig({
      raw: state.configRaw,
      originalRaw: state.configRawOriginal,
      valid: state.configValid,
      issues: state.configIssues,
      loading: state.configLoading,
      saving: state.configSaving,
      applying: state.configApplying,
      updating: state.updateRunning,
      connected: state.connected,
      schema: state.configSchema,
      schemaLoading: state.configSchemaLoading,
      uiHints: state.configUiHints,
      formMode: state.configFormMode,
      formValue: state.configForm,
      originalValue: state.configFormOriginal,
      searchQuery: state.configSearchQuery,
      activeSection: state.configActiveSection,
      activeSubsection: state.configActiveSubsection,
      onRawChange: (next) => {
        state.configRaw = next;
      },
      onFormModeChange: (mode) => (state.configFormMode = mode),
      onFormPatch: (path, value) => updateConfigFormValue(state, path, value),
      onSearchChange: (query) => (state.configSearchQuery = query),
      onSectionChange: (section) => {
        state.configActiveSection = section;
        state.configActiveSubsection = null;
      },
      onSubsectionChange: (section) => (state.configActiveSubsection = section),
      onReload: () => loadConfig(state),
      onSave: () => saveConfig(state),
      onApply: () => applyConfig(state),
      onUpdate: () => runUpdate(state),
    });
  }

  // ── Tasks Board (MC-native) ─────────────────────────────────────
  if (state.tab === "mc-tasks") {
    const doLoadTasks = async () => {
      if (!state.client || !state.connected) {
        console.warn("[CF] mc-tasks: client=", !!state.client, "connected=", state.connected);
        state.cfTasksError = "Not connected to gateway";
        return;
      }
      if (state.cfTasks.length === 0) {
        state.cfTasksLoading = true;
      }
      state.cfTasksError = null;
      try {
        const res = await state.client.request<{ tasks: unknown[] }>("contentforge.tasks.list", {});
        console.log("[CF] tasks loaded:", res);
        state.cfTasks = (res?.tasks ?? []) as CfTask[];
      } catch (err) {
        console.error("[CF] tasks load error:", err);
        state.cfTasksError = String(err);
      } finally {
        state.cfTasksLoading = false;
      }
    };
    return renderCfTasks({
      tasks: state.cfTasks,
      loading: state.cfTasksLoading,
      error: state.cfTasksError,
      onRefresh: () => void doLoadTasks(),
      onCreate: async (title, agentType) => {
        if (!state.client || !state.connected) {
          state.cfTasksError = "Not connected to gateway";
          return;
        }
        try {
          console.log("[CF] creating task:", { title, agentType });
          await state.client.request("contentforge.tasks.create", { title, agentType });
          await doLoadTasks();
        } catch (err) {
          console.error("[CF] create task error:", err);
          state.cfTasksError = String(err);
        }
      },
    });
  }

  // ── Content Pipeline (MC-native) ────────────────────────────────
  if (state.tab === "mc-content") {
    const doLoadKeywords = async () => {
      if (!state.client || !state.connected) {
        state.cfKeywordsError = "Not connected to gateway";
        return;
      }
      if (state.cfKeywords.length === 0) {
        state.cfKeywordsLoading = true;
      }
      state.cfKeywordsError = null;
      try {
        const res = await state.client.request<{ keywords: unknown[] }>(
          "contentforge.keywords.list",
          {},
        );
        console.log("[CF] keywords loaded:", res);
        state.cfKeywords = (res?.keywords ?? []) as CfKeyword[];
      } catch (err) {
        console.error("[CF] keywords load error:", err);
        state.cfKeywordsError = String(err);
      } finally {
        state.cfKeywordsLoading = false;
      }
      // Also load CSV sources
      try {
        const csvRes = await state.client.request<{ csvSources: CfCsvSource[] }>(
          "contentforge.csv.list",
          { siteId: state.activeSiteId || "default" },
        );
        const sources = csvRes?.csvSources ?? [];
        (state as Record<string, unknown>).cfCsvSources = sources;
        // Compute total from active sources instead of loading all products
        (state as Record<string, unknown>).cfTotalProducts = sources
          .filter((s: CfCsvSource) => s.active)
          .reduce((sum: number, s: CfCsvSource) => sum + (s.productCount || 0), 0);
      } catch {
        /* ignore csv load errors */
      }
    };
    // Auto-load on first render (keywords empty = never loaded yet)
    if (
      state.cfKeywords.length === 0 &&
      !state.cfKeywordsLoading &&
      state.client &&
      state.connected
    ) {
      void doLoadKeywords();
    }
    // Filter keywords by active project (null = all projects)
    const filteredKeywords = state.activeSiteId
      ? state.cfKeywords.filter((k) => k.siteId === state.activeSiteId)
      : state.cfKeywords;

    return renderCfContent({
      keywords: filteredKeywords,
      loading: state.cfKeywordsLoading,
      error: state.cfKeywordsError,
      csvSources:
        ((state as Record<string, unknown>).cfCsvSources as CfCsvSource[] | undefined) ?? [],
      totalProducts:
        ((state as Record<string, unknown>).cfTotalProducts as number | undefined) ?? 0,
      onRefresh: () => void doLoadKeywords(),
      activeSiteId: state.activeSiteId || "default",
      onAddKeywords: async (siteId, texts, force, keywordType) => {
        if (!state.client) {
          return;
        }
        try {
          console.log("[CF] adding keywords:", { siteId, texts, force, keywordType });
          const res = await state.client.request<{
            count: number;
            skipped: number;
            skippedTexts?: string[];
          }>("contentforge.keywords.add", { siteId, texts, force: force ?? false, keywordType });
          await doLoadKeywords();

          // Notify user about results
          if (res && res.skipped > 0) {
            const skippedList = res.skippedTexts?.join("\n• ") ?? "";
            alert(
              `✅ Added ${res.count} keyword(s)\n⚠️ Skipped ${res.skipped} similar keyword(s):\n• ${skippedList}`,
            );
          }
        } catch (err) {
          console.error("[CF] add keywords error:", err);
          state.cfKeywordsError = String(err);
        }
      },
      onAddReverseKeywords: async (siteId, texts, productUrls) => {
        if (!state.client) {
          return;
        }
        try {
          console.log("[CF] adding reverse keywords:", { siteId, texts, productUrls });
          await state.client.request("contentforge.keywords.addReverse", {
            siteId,
            texts,
            productUrls,
          });
          await doLoadKeywords();
        } catch (err) {
          console.error("[CF] add reverse keywords error:", err);
          state.cfKeywordsError = String(err);
        }
      },
      onRemoveKeyword: async (id) => {
        if (!state.client) {
          return;
        }
        try {
          await state.client.request("contentforge.keywords.remove", { id });
          await doLoadKeywords();
        } catch (err) {
          state.cfKeywordsError = String(err);
        }
      },
      onUpdateStage: async (id, stage) => {
        if (!state.client) {
          return;
        }
        try {
          await state.client.request("contentforge.keywords.updateStage", { id, stage });
          await doLoadKeywords();
        } catch (err) {
          state.cfKeywordsError = String(err);
        }
      },
      onRunStrategist: async (keywordId) => {
        if (!state.client) {
          return;
        }
        try {
          state.cfKeywordsError = null;
          await state.client.request("contentforge.strategist.run", { keywordId });
          await doLoadKeywords();
        } catch (err) {
          state.cfKeywordsError = String(err);
        }
      },
      onRunSourcer: async (keywordId) => {
        if (!state.client) {
          return;
        }
        try {
          state.cfKeywordsError = null;
          await state.client.request("contentforge.sourcer.run", { keywordId });
          await doLoadKeywords();
        } catch (err) {
          state.cfKeywordsError = String(err);
        }
      },
      onRunWriter: async (keywordId) => {
        if (!state.client) {
          return;
        }
        try {
          state.cfKeywordsError = null;
          await state.client.request("contentforge.writer.run", { keywordId });
          await doLoadKeywords();
        } catch (err) {
          state.cfKeywordsError = String(err);
        }
      },
      onRepublish: async (keywordId) => {
        if (!state.client) {
          return;
        }
        try {
          state.cfKeywordsError = null;
          const result = await state.client.request<{ ok: boolean; url?: string }>(
            "contentforge.article.republish",
            { keywordId },
          );
          if (result?.ok) {
            alert(`✅ Republished successfully: ${result.url ?? ""}`);
          }
          await doLoadKeywords();
        } catch (err) {
          state.cfKeywordsError = `Republish failed: ${String(err)}`;
        }
      },
      onUploadCsv: async (filename, content) => {
        if (!state.client) {
          return;
        }
        try {
          state.cfKeywordsError = null;
          await state.client.request("contentforge.csv.upload", {
            siteId: state.activeSiteId || "default",
            filename,
            content,
          });
          await doLoadKeywords();
        } catch (err) {
          state.cfKeywordsError = String(err);
        }
      },
      onToggleCsv: async (id, active) => {
        if (!state.client) {
          return;
        }
        try {
          await state.client.request("contentforge.csv.toggle", { id, active });
          await doLoadKeywords();
        } catch (err) {
          state.cfKeywordsError = String(err);
        }
      },
      onDeleteCsv: async (id) => {
        if (!state.client) {
          return;
        }
        try {
          await state.client.request("contentforge.csv.delete", { id });
          await doLoadKeywords();
        } catch (err) {
          state.cfKeywordsError = String(err);
        }
      },
      onRegenerateImage: async (keywordId, customPrompt) => {
        if (!state.client) {
          return;
        }
        try {
          state.cfKeywordsError = null;
          await state.client.request("contentforge.article.regenerateImage", {
            keywordId,
            customPrompt: customPrompt || undefined,
          });
          await doLoadKeywords();
        } catch (err) {
          state.cfKeywordsError = `Image regeneration failed: ${String(err)}`;
        }
      },
    });
  }

  // ── Approvals (MC-native) ───────────────────────────────────────
  if (state.tab === "mc-approvals") {
    const doLoadApprovals = async () => {
      if (!state.client || !state.connected) {
        state.cfApprovalsError = "Not connected to gateway";
        return;
      }
      if (state.cfApprovals.length === 0) {
        state.cfApprovalsLoading = true;
      }
      state.cfApprovalsError = null;
      try {
        const res = await state.client.request<{ approvals: unknown[] }>(
          "contentforge.approvals.list",
          { siteId: state.activeSiteId || undefined },
        );
        console.log("[CF] approvals loaded:", res);
        state.cfApprovals = (res?.approvals ?? []) as CfApproval[];
      } catch (err) {
        console.error("[CF] approvals load error:", err);
        state.cfApprovalsError = String(err);
      } finally {
        state.cfApprovalsLoading = false;
      }
    };

    // Auto-load on first visit or when project changes (only if connected)
    const currentSiteKey = state.activeSiteId ?? "__all__";
    const stateRec = state as Record<string, unknown>;
    if (state.client && state.connected && stateRec._approvalsLoadedForSiteId !== currentSiteKey) {
      stateRec._approvalsLoadedForSiteId = currentSiteKey;
      void doLoadApprovals();
    }

    return renderCfApprovals({
      approvals: state.cfApprovals as CfApproval[],
      loading: state.cfApprovalsLoading,
      error: state.cfApprovalsError,
      activeCategory: (state.cfApprovalsCategory ?? "all") as CfApprovalCategory | "all",
      previewHtml:
        ((state as Record<string, unknown>).cfArticlePreviewHtml as string | null) ?? null,
      onRefresh: () => void doLoadApprovals(),
      onResolve: async (id, decision, note) => {
        if (!state.client) {
          return;
        }
        try {
          await state.client.request("contentforge.approvals.resolve", {
            id,
            status: decision,
            note,
          });
          await doLoadApprovals();
        } catch (err) {
          state.cfApprovalsError = String(err);
        }
      },
      onCategoryChange: (category) => {
        state.cfApprovalsCategory = category;
      },
      onPreviewArticle: async (articleId) => {
        if (!state.client) {
          return;
        }
        try {
          const result = await state.client.request("contentforge.article.get", { articleId });
          const title = ((result as Record<string, unknown>).title as string) ?? "Article Preview";
          const htmlContent = ((result as Record<string, unknown>).htmlContent as string) ?? "";
          const featuredImg =
            ((result as Record<string, unknown>).featuredImageBase64 as string) ?? "";
          // Wrap in a basic HTML page with white background
          const fullHtml = `<!DOCTYPE html>
<html lang="ro">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 800px; margin: 0 auto; padding: 24px; color: #1a202c; background: #fff; line-height: 1.7; }
    h1 { color: #192a3d; font-size: 1.8rem; margin-bottom: 16px; }
    .featured-img { width: 100%; height: auto; border-radius: 12px; margin-bottom: 24px; }
    h2 { color: #192a3d; font-size: 1.3rem; margin-top: 32px; border-top: 1px solid #e2e8f0; padding-top: 16px; }
    h3 { color: #2d3748; font-size: 1.1rem; }
    p { margin: 12px 0; }
    a { color: #2563eb; }
    ul { padding-left: 20px; }
    li { margin: 4px 0; }
    table { border-collapse: collapse; width: 100%; margin: 16px 0; }
    th, td { border: 1px solid #e2e8f0; padding: 8px 12px; text-align: left; }
    th { background: #f7fafc; font-weight: 600; }
    blockquote { border-left: 4px solid #22c55e; margin: 16px 0; padding: 12px 20px; background: #f0fdf4; }
    img { max-width: 100%; height: auto; }
  </style>
</head>
<body>
  <h1>${title}</h1>
  ${featuredImg ? `<img class="featured-img" src="${featuredImg}" alt="${title}" />` : ""}
  ${htmlContent}
</body>
</html>`;
          (state as Record<string, unknown>).cfArticlePreviewHtml = fullHtml;
        } catch (err) {
          state.cfApprovalsError = `Preview failed: ${String(err)}`;
        }
      },
      onPreviewOutline: (data: Record<string, unknown>) => {
        const title = (data.title as string) ?? "Outline";
        const meta = (data.metaDescription as string) ?? "";
        const articleType = (data.articleType as string) ?? "problem";
        const intro = (data.introduction as string) ?? "";
        const conclusion = (data.conclusion as string) ?? "";
        const sections = (data.sections as Array<{ h2: string; bullets: string[] }>) ?? [];
        const keywords = (data.semanticKeywords as string[]) ?? [];

        const sectionsHtml = sections
          .map((s) => {
            const bulletsHtml = (s.bullets ?? []).map((b: string) => `<li>${b}</li>`).join("\n");
            return `<div style="margin:20px 0;padding:16px;background:#f8fafc;border-radius:8px;border-left:4px solid #818cf8">
            <h2 style="color:#192a3d;font-size:1.1rem;margin:0 0 12px">${s.h2}</h2>
            <ul style="margin:0;padding-left:20px;color:#4a5568">${bulletsHtml}</ul>
          </div>`;
          })
          .join("\n");

        const keywordsHtml =
          keywords.length > 0
            ? `<div style="margin-top:24px;padding:16px;background:#f0fdf4;border-radius:8px">
              <h3 style="color:#166534;font-size:0.95rem;margin:0 0 8px">Cuvinte cheie semantice (${keywords.length})</h3>
              <p style="color:#4a5568;font-size:0.85rem;line-height:1.8;margin:0">${keywords.join(", ")}</p>
            </div>`
            : "";

        const fullHtml = `<!DOCTYPE html>
<html lang="ro"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0">
<style>body{font-family:'Segoe UI',Tahoma,Verdana,sans-serif;max-width:800px;margin:0 auto;padding:24px;color:#1a202c;background:#fff;line-height:1.7}
h1{color:#192a3d;font-size:1.6rem;margin-bottom:8px}
.badge{display:inline-block;padding:4px 12px;border-radius:12px;font-size:0.75rem;font-weight:700;text-transform:uppercase;margin-bottom:16px}
.badge-product{background:#fef3c7;color:#92400e}
.badge-problem{background:#dbeafe;color:#1e40af}
.meta{color:#64748b;font-size:0.9rem;font-style:italic;padding:12px;background:#f1f5f9;border-radius:8px;margin:12px 0 24px}
.intro{color:#334155;padding:16px;background:#fefce8;border-radius:8px;border-left:4px solid #fbbf24;margin-bottom:24px}
.conclusion{color:#334155;padding:16px;background:#eff6ff;border-radius:8px;border-left:4px solid #3b82f6;margin-top:24px}
</style></head><body>
<h1>${title}</h1>
<span class="badge ${articleType === "product" ? "badge-product" : "badge-problem"}">${articleType === "product" ? "📦 Articol Produs" : "🩺 Articol Problemă"}</span>
<div class="meta"><strong>Meta:</strong> ${meta}</div>
${intro ? `<div class="intro"><strong>Introducere:</strong><br>${intro}</div>` : ""}
<h2 style="color:#4338ca;font-size:1.2rem;margin:24px 0 16px">Structură H2</h2>
${sectionsHtml}
${conclusion ? `<div class="conclusion"><strong>Concluzie:</strong><br>${conclusion}</div>` : ""}
${keywordsHtml}
</body></html>`;
        (state as Record<string, unknown>).cfArticlePreviewHtml = fullHtml;
      },
      onRegenerateImage: async (articleId, customPrompt) => {
        if (!state.client) {
          return;
        }
        try {
          state.cfApprovalsError = null;
          await state.client.request("contentforge.article.regenerateImage", {
            articleId,
            customPrompt: customPrompt || undefined,
          });
          // Clear preview so it reloads with the new image
          (state as Record<string, unknown>).cfArticlePreviewHtml = null;
          await doLoadApprovals();
        } catch (err) {
          state.cfApprovalsError = `Image regeneration failed: ${String(err)}`;
        }
      },
      onClosePreview: () => {
        (state as Record<string, unknown>).cfArticlePreviewHtml = null;
      },
    });
  }

  // ── Calendar (MC-native) ────────────────────────────────────────
  if (state.tab === "mc-calendar") {
    return renderCfCalendar(
      {
        client: state.client,
        activeSiteId: state.activeSiteId || null,
        onRefresh: () => {},
      },
      () => void 0, // rerender is handled by reactive state
    );
  }

  // ── Memory (MC-native) ─────────────────────────────────────────
  if (state.tab === "mc-memory") {
    return renderCfMemory(
      {
        client: state.client,
        onRefresh: () => {},
      },
      () => void 0,
    );
  }

  // ── Products (MC-native) ─────────────────────────────────────────
  if (state.tab === "mc-products") {
    return renderCfProducts(
      {
        client: state.client,
        activeSiteId: state.activeSiteId || null,
        onRefresh: () => {},
      },
      () => void 0,
    );
  }

  // ── Projects (MC-native) ────────────────────────────────────────
  if (state.tab === "mc-projects") {
    const doLoadProjects = async () => {
      if (!state.client || !state.connected) {
        state.cfProjectsError = "Not connected to gateway";
        return;
      }
      state.cfProjectsLoading = true;
      state.cfProjectsError = null;
      try {
        const res = await state.client.request<{ projects: unknown[] }>(
          "contentforge.projects.list",
          {},
        );
        console.log("[CF] projects loaded:", res);
        state.cfProjects = (res?.projects ?? []) as CfProject[];
      } catch (err) {
        console.error("[CF] projects load error:", err);
        state.cfProjectsError = String(err);
      } finally {
        state.cfProjectsLoading = false;
      }
    };
    return renderCfProjects({
      projects: state.cfProjects as CfProject[],
      keywords: state.cfKeywords,
      loading: state.cfProjectsLoading,
      error: state.cfProjectsError,
      selectedProjectId: state.cfSelectedProjectId,
      onRefresh: () => void doLoadProjects(),
      onCreate: async (name, description) => {
        if (!state.client) {
          return;
        }
        try {
          await state.client.request("contentforge.projects.create", {
            name,
            description,
            siteId: "default",
          });
          await doLoadProjects();
        } catch (err) {
          state.cfProjectsError = String(err);
        }
      },
      onAssignKeyword: async (projectId, keywordId) => {
        if (!state.client) {
          return;
        }
        try {
          await state.client.request("contentforge.projects.assignKeyword", {
            projectId,
            keywordId,
          });
          await doLoadProjects();
        } catch (err) {
          state.cfProjectsError = String(err);
        }
      },
      onSelectProject: (projectId) => {
        state.cfSelectedProjectId = projectId;
      },
    });
  }

  // ── Team view → ContentForge Team ──────────────────────────────
  if (state.tab === "mc-team") {
    return renderContentForge({
      loading: state.contentforgeLoading,
      error: state.contentforgeError,
      dashboard: state.contentforgeDashboard,
      subView: "team",
      onSubViewChange: () => {},
      onRefresh: () => state.loadContentForge(),
      client: state.client,
      activeSiteId: state.activeSiteId,
    });
  }

  // ── Office → Virtual Office ────────────────────────────────────
  // ── Pinterest → boards, pin queue, drip pacing, analytics ──────
  if (state.tab === "mc-pinterest") {
    const doLoadPinterest = async () => {
      if (!state.client || !state.connected) {
        state.contentforgeError = "Not connected to gateway";
        return;
      }
      state.contentforgeLoading = true;
      state.contentforgeError = null;
      try {
        const res = await state.client.request<ContentForgeDashboardData>(
          "contentforge.dashboard",
          {},
        );
        if (res) {
          state.contentforgeDashboard = res;
        }
      } catch (err) {
        state.contentforgeError = String(err);
      } finally {
        state.contentforgeLoading = false;
      }
    };
    const stateAny = state as Record<string, unknown>;
    if (!stateAny._pinterestPolling) {
      void doLoadPinterest();
      stateAny._pinterestPolling = setInterval(() => void doLoadPinterest(), 5000);
    }
    // Respect the site switcher: with several Pinterest-enabled sites, show
    // only the active one (all of them when no site is selected).
    const allPinSites = state.contentforgeDashboard?.pinterest ?? [];
    const pinSites = state.activeSiteId
      ? allPinSites.filter((s) => s.siteId === state.activeSiteId)
      : allPinSites;
    return html`
      <div class="cf-shell">
        <div style="display:flex;align-items:baseline;gap:12px;margin-bottom:4px">
          <h1 style="font-size:22px;margin:0">📌 Pinterest</h1>
          <span style="font-size:12.5px;opacity:.6">boards · pin queue · drip pacing · analytics</span>
        </div>
        ${
          state.contentforgeError
            ? html`<div style="color:#b4462f;padding:12px 0">${state.contentforgeError}</div>`
            : nothing
        }
        ${
          pinSites.length === 0 && !state.contentforgeLoading
            ? html`
                <div style="opacity: 0.65; padding: 24px 0">
                  No sites with Pinterest automation enabled. Turn it on in Settings → 📌 Pinterest.
                </div>
              `
            : nothing
        }
        ${pinSites.map((s) => renderPinterestPanel(s, state.client, () => void doLoadPinterest()))}
      </div>
    `;
  } else {
    const stateAny = state as Record<string, unknown>;
    if (stateAny._pinterestPolling) {
      clearInterval(stateAny._pinterestPolling as ReturnType<typeof setInterval>);
      stateAny._pinterestPolling = null;
    }
  }

  if (state.tab === "mc-office") {
    const doLoadDashboard = async () => {
      if (!state.client || !state.connected) {
        state.contentforgeError = "Not connected to gateway";
        return;
      }
      state.contentforgeLoading = true;
      state.contentforgeError = null;
      try {
        const res = await state.client.request<ContentForgeDashboardData>(
          "contentforge.dashboard",
          {},
        );
        if (res) {
          state.contentforgeDashboard = res;
        }
      } catch (err) {
        state.contentforgeError = String(err);
      } finally {
        state.contentforgeLoading = false;
      }
    };

    // Auto-poll every 5 seconds while Office tab is active
    const stateAny = state as Record<string, unknown>;
    if (!stateAny._officePolling) {
      void doLoadDashboard(); // initial load
      stateAny._officePolling = setInterval(() => void doLoadDashboard(), 5000);
    }

    return renderCfOffice({
      agents: state.contentforgeDashboard?.agentStates ?? [],
      recentActivity: state.contentforgeDashboard?.recentActivity ?? [],
      arthurPaused: state.contentforgeDashboard?.arthurPaused ?? true,
      loading: state.contentforgeLoading,
      error: state.contentforgeError,
      onRefresh: () => void doLoadDashboard(),
    });
  } else {
    // Clear polling when leaving Office tab
    const stateAny = state as Record<string, unknown>;
    if (stateAny._officePolling) {
      clearInterval(stateAny._officePolling as ReturnType<typeof setInterval>);
      stateAny._officePolling = null;
    }
  }

  // ── Settings → ContentForge Settings ───────────────────────────
  if (state.tab === "mc-settings") {
    return renderContentForge({
      loading: state.contentforgeLoading,
      error: state.contentforgeError,
      dashboard: state.contentforgeDashboard,
      subView: "settings",
      onSubViewChange: () => {},
      onRefresh: () => state.loadContentForge(),
      client: state.client,
      activeSiteId: state.activeSiteId,
      createMode: ((state as Record<string, unknown>).cfCreatingProject as boolean) ?? false,
      onProjectCreated: (newSiteId: string) => {
        // Switch to the new project
        state.activeSiteId = newSiteId;
        localStorage.setItem("cf_activeSiteId", newSiteId);
        (state as Record<string, unknown>).cfCreatingProject = false;
        // Reload sites list for the project switcher
        void state.client
          ?.request<{ sites: unknown[] }>("contentforge.sites.list", {})
          .then((res) => {
            state.cfSites = (res?.sites ?? []) as typeof state.cfSites;
          });
      },
    });
  }

  // ── MC-only views (placeholder) ────────────────────────────────
  return renderMcPlaceholder(state.tab);
}

function renderMcPlaceholder(tab: Tab) {
  const info: Record<string, { title: string; desc: string }> = {
    "mc-tasks": {
      title: "Tasks",
      desc: "Kanban board for all work items. Coming in Phase 3.",
    },
    "mc-content": {
      title: "Content",
      desc: "Content pipeline from keyword to published article. Coming in Phase 3.",
    },
    "mc-approvals": {
      title: "Approvals",
      desc: "Review queue for drafts, outlines, and products. Coming in Phase 3b.",
    },
    "mc-council": {
      title: "Council",
      desc: "Chat with agents. Wraps the existing OpenClaw Chat view.",
    },
    "mc-calendar": {
      title: "Calendar",
      desc: "Month and week views showing articles by status and date.",
    },
    "mc-projects": {
      title: "Projects",
      desc: "Group keywords by campaign or project. Coming in Phase 3b.",
    },
    "mc-memory": {
      title: "Memory",
      desc: "Search agent memories and browse the activity log.",
    },
    "mc-products": {
      title: "Products",
      desc: "Browse product inventory, stock status, and source management.",
    },
    "mc-docs": {
      title: "Docs",
      desc: "Documentation and knowledge base.",
    },
    "mc-people": {
      title: "People",
      desc: "Contacts and channels. Wraps the existing Channels view.",
    },
    "mc-office": {
      title: "Office",
      desc: "Virtual pixel-art office with live agent status. Coming in Phase 11b.",
    },
    "mc-team": {
      title: "Team",
      desc: "Agent roster with profiles. Wraps the existing Agents view.",
    },
    "mc-usage": {
      title: "Usage",
      desc: "Token usage and cost tracking. Wraps the existing Usage view.",
    },
    "mc-settings": {
      title: "Settings",
      desc: "Configuration, API keys, and rate limits. Wraps the existing Config view.",
    },
    contentforge: {
      title: "Mission Control",
      desc: "ContentForge dashboard.",
    },
  };
  const entry = info[tab] ?? { title: "View", desc: "Coming soon." };
  return html`
    <div class="cf-empty">
      <div class="cf-empty__title">${entry.title}</div>
      <div class="cf-empty__text">${entry.desc}</div>
    </div>
  `;
}
